import dcmjs from 'dcmjs';
import StaticWadoClient from '../qido/StaticWadoClient';
import DICOMWeb from '../../../DICOMWeb/';
import RetrieveMetadataLoader from './retrieveMetadataLoader';
import { sortStudySeries, sortingCriteria } from '../../sortStudy';
import getSeriesInfo from '../../getSeriesInfo';
import {
  createStudyFromSOPInstanceList,
  addInstancesToStudy,
} from './studyInstanceHelpers';

import errorHandler from '../../../errorHandler';
import { getXHRRetryRequestHook } from '../../../utils/xhrRetryRequestHook';

const { naturalizeDataset } = dcmjs.data.DicomMetaDictionary;

/**
 * Map series to an array of SeriesInstanceUID
 * @param {Arrays} series list of Series Instance UIDs
 * @returns {Arrays} A list of Series Instance UIDs
 */
function mapStudySeries(series) {
  return series.map(series => getSeriesInfo(series).SeriesInstanceUID);
}

/**
 *
 * @param server
 * @param study
 * @param seriesLoader
 * @returns {boolean|*|null}
 */
function attachSeriesLoader(server, study, seriesLoader) {
  study.seriesLoader = Object.freeze({
    hasNext() {
      return seriesLoader.hasNext();
    },
    async next() {
      while (seriesLoader.hasNext()) {
        const series = await seriesLoader.next();
        const sopInstanceList = series && series.sopInstances;

        if (!Array.isArray(sopInstanceList) || sopInstanceList.length === 0) {
          console.log('Skipping a series without valid metadata.');
          continue;
        }

        await addInstancesToStudy(server, study, sopInstanceList);
        return study.seriesMap[series.seriesInstanceUID];
      }

      console.log('There are no more valid series to load.');
      return null;
    },
  });
}

/**
 * It creates a robust and asynchronous series loader.
 * If the series metadata fails on loading, it logs the error
 * and automatically tries to load the next one.
 * @returns {Object} A loader with iterator interface (hasNext, next).
 */
function makeSeriesAsyncLoader(
  dicomWebClient,
  studyInstanceUID,
  seriesInstanceUIDList
) {
  // Start every metadata request now. The consumer can await individual
  // promises later, but no series request is held back by the first one.
  const seriesRequests = seriesInstanceUIDList.map((seriesInstanceUID) => {
    console.log(`Trying to download metadata from the series: ${seriesInstanceUID}`);

    return dicomWebClient.retrieveSeriesMetadata({
      studyInstanceUID,
      seriesInstanceUID,
    }).then((sopInstances) => {
      console.log(`Series successfully downloaded: ${seriesInstanceUID}`);
      return { studyInstanceUID, seriesInstanceUID, sopInstances };
    }).catch((err) => {
      console.error(
        `Error on metadata download for the serie ${seriesInstanceUID}. Skipping to the next one.`,
        err
      );
      return null;
    });
  });

  return Object.freeze({
    // hasNext works the same way, it returns if there are remaining series promises.
    hasNext() {
      return seriesRequests.length > 0;
    },

    /**
     * It loads the next available serie, omitting those that fail
     * @returns {Promise<Object|null>} Returns the loaded serie or null if it's not possible to load any other.
     */
    async next() {
      // While there are series metadata promises to consume...
      while (this.hasNext()) {
        const series = await seriesRequests.shift();

        if (series) {
          return series;
        }
      }

      // If the loop ends, it means that there are no more series in the list (or all them failed).
      // We return null to denote that there are no more data to load.
      return null;
    },
  });
}

/**
 * Class for async load of study metadata.
 * It inherits from RetrieveMetadataLoader
 *
 * It loads the one series and then append to seriesLoader the others to be consumed/loaded
 */
export default class RetrieveMetadataLoaderAsync extends RetrieveMetadataLoader {
  configLoad() {
    const { server } = this;

    const client = new StaticWadoClient({
      ...server,
      url: server.wadoRoot,
      headers: DICOMWeb.getAuthorizationHeader(server),
      errorInterceptor: errorHandler.getHTTPErrorHandler(),
      requestHooks: [getXHRRetryRequestHook()],
    });

    client.qidoURL = server.qidoRoot;
    client.seriesExtraFilters = server.seriesExtraFilters ?? null;

    this.client = client;
  }

  /**
   * @returns {Array} Array of preLoaders. To be consumed as queue
   */
  * getPreLoaders() {
    const preLoaders = [];
    const {
      studyInstanceUID,
      filters: { seriesInstanceUID } = {},
      client,
    } = this;

    if (seriesInstanceUID) {
      const options = {
        studyInstanceUID,
        queryParams: { SeriesInstanceUID: seriesInstanceUID },
      };
      preLoaders.push(client.searchForSeries.bind(client, options));
    }

    // Alma - Patch to include filters on series call. All this part needs a full refactor
    const options = { studyInstanceUID };
    if (client.seriesExtraFilters) {
      options.queryParams = client.seriesExtraFilters;
    }

    // Fallback preloader
    preLoaders.push(client.searchForSeries.bind(client, options));

    yield* preLoaders;
  }

  async preLoad() {
    const { server, studyInstanceUID, filters, client } = this;
    let seriesData;

    if (filters?.seriesInstanceUID) {
      // A selected-series route must never fall back to an unfiltered query.
      // Query each UID separately because some PACS do not accept multi-value
      // SeriesInstanceUID matching in one QIDO request.
      const seriesInstanceUIDs = filters.seriesInstanceUID.split(',').filter(Boolean);
      const seriesResults = await Promise.all(seriesInstanceUIDs.map((seriesInstanceUID) => client.searchForSeries({
        studyInstanceUID,
        queryParams: {
          ...(server.seriesExtraFilters || {}),
          SeriesInstanceUID: seriesInstanceUID,
        },
      })));
      seriesData = seriesResults.flat();
    } else {
      const preLoaders = this.getPreLoaders();
      // seriesData is the result of the QIDO-RS Search For Series request.
      seriesData = await this.runLoaders(preLoaders);
    }

    const seriesSorted = sortStudySeries(
      seriesData,
      sortingCriteria.seriesSortCriteria.seriesInfoSortingCriteria
    );
    const seriesInstanceUIDsMap = mapStudySeries(seriesSorted);

    return {
      seriesInstanceUIDsMap,
      seriesData,
    };
  }

  async load(preLoadData) {
    const { client, studyInstanceUID } = this;

    const seriesAsyncLoader = makeSeriesAsyncLoader(
      client,
      studyInstanceUID,
      preLoadData.seriesInstanceUIDsMap
    );

    const firstSeries = await seriesAsyncLoader.next();

    // ADD THIS VERIFICATION:
    // If the first serie (and maybe others) failed, firstSeries can be null.
    // If so, we can't continue loading.
    if (!firstSeries) {
      // We throw an custom error to notify the user.
      errorHandler.getHTTPErrorHandler()({
        message: 'No series information could be loaded for this study.',
        status: 'No Series',
      });
      // We throw an exception to stop the process in a controlled manner.
      throw new Error('The metadata for the first series of the study could not be loaded.');
    }

    return {
      sopInstances: firstSeries.sopInstances,
      asyncLoader: seriesAsyncLoader,
      seriesData: preLoadData.seriesData,
    };
  }

  async posLoad(loadData) {
    const { server } = this;

    const { sopInstances, asyncLoader, seriesData } = loadData;

    const study = await createStudyFromSOPInstanceList(server, sopInstances);

    // TODO: Should this be in a helper
    const seriesDataNaturalized = seriesData.map(naturalizeDataset);

    seriesDataNaturalized.forEach((series, idx) => {
      const seriesDataFromQIDO = {
        SeriesInstanceUID: series.SeriesInstanceUID,
        SeriesDescription: series.SeriesDescription,
        SeriesNumber: series.SeriesNumber,
        Modality: series.Modality,
        instances: [],
      };

      if (study.series[idx]) {
        study.series[idx] = Object.assign(
          seriesDataFromQIDO,
          study.series[idx]
        );
      } else {
        study.series[idx] = seriesDataFromQIDO;
      }

      study.seriesMap[series.SeriesInstanceUID] = study.series[idx];
    });

    if (asyncLoader.hasNext()) {
      attachSeriesLoader(server, study, asyncLoader);
    }

    return study;
  }
}
